import {
  ScoringRunStatus,
  type StudyRunCaseDashboardMissionDto,
  type StudyRunCaseDashboardSelectDto,
  type StudyRunCaseDashboardThreadDetailDto,
  type StudyRunCaseDashboardThreadVerdictDto,
  type WorkflowGateAuditSelectDto,
} from '@@types/api.types';
import { Tile } from '@components/shared/tile.component';
import { errorToast, successToast } from '@hooks/toast.hook';
import {
  useCreateMeiaRunCase,
  useGetMeiaGateAudit,
  useGetMeiaRunCaseDashboard,
  useGetMeiaRunCases,
  useGetMeiaStudy,
  useRunMeiaRunCase,
  useUpdateMeiaStudy,
} from '@queries/meia.query';
import { type MeiaRunEpochFormValues } from '@schemas/meia.schema';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { MeiaP4GateStatusPanel } from './meia-p4-gate-status-panel.component';
import { MeiaP4KillwebVerdictTable } from './meia-p4-killweb-verdict-table.component';
import { ThreadVerdictPicker } from './meia-p4-pickers.component';
import { RunCaseDashboard, type RunCaseDashboardView } from './meia-p4-run-case-dashboard.component';
import { RunCaseHeader, runStatusLabel } from './meia-p4-run-case-header.component';
import { formatRunCaseLabel, getDefaultActiveThreadId } from './meia-p4-run-cases.util';
import {
  DEFAULT_RUN_EPOCH,
  DEFAULT_SCORING_CONFIG,
  MeiaScoringConfigModal,
} from './meia-p4-scoring-config-modal.component';
import { getMeiaPhaseReadinessStatus } from './meia-phase-readiness-status.util';

type MeiaP4RunCasesViewProps = {
  canEdit: boolean;
  missionId?: string;
  studyId: string;
  view?: RunCaseDashboardView;
};

export const MeiaP4RunCasesView: FC<MeiaP4RunCasesViewProps> = (props) => {
  const { canEdit, missionId: scopedMissionId, studyId, view = 'runcases' } = props;
  const [activeMissionId, setActiveMissionId] = useState<string | null>(null);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isScoringConfigModalOpen, setIsScoringConfigModalOpen] = useState(false);
  const [selectedRunCaseId, setSelectedRunCaseId] = useState<string | null>(null);

  const { data: study } = useGetMeiaStudy({ studyId });
  const {
    data: runCases = [],
    isLoading: isRunCasesLoading,
    refetch: refetchRunCases,
  } = useGetMeiaRunCases({ missionId: scopedMissionId, studyId });
  const { data: p2Audit } = useGetMeiaGateAudit({ gate: 'P2', missionId: scopedMissionId, studyId });
  const { data: p3Audit } = useGetMeiaGateAudit({ gate: 'P3', missionId: scopedMissionId, studyId });
  const { isPending: isSavingScoringConfig, mutateAsync: updateStudy } = useUpdateMeiaStudy();
  const { isPending: isCreatingRunCase, mutateAsync: createRunCase } = useCreateMeiaRunCase();
  const selectedRunCase = useMemo(
    () => runCases.find((runCase) => runCase.id === selectedRunCaseId) ?? runCases[0],
    [runCases, selectedRunCaseId],
  );
  const { data: dashboard, isLoading: isDashboardLoading } = useGetMeiaRunCaseDashboard({
    runCaseId: selectedRunCase?.id,
    studyId,
  });
  const { isPending: isRunning, mutateAsync: runCase, variables: runningVariables } = useRunMeiaRunCase();

  const activeMissionDashboard = useMemo(
    () => getActiveMissionDashboard({ activeMissionId, dashboard, fallbackToFirst: !scopedMissionId }),
    [activeMissionId, dashboard, scopedMissionId],
  );
  const activeThreadDashboard = useMemo(
    () => getActiveThreadDashboard({ activeMission: activeMissionDashboard, activeThreadId }),
    [activeMissionDashboard, activeThreadId],
  );
  const activeThreadVerdict = useMemo(
    () => getActiveThreadVerdict({ activeMission: activeMissionDashboard, activeThreadId }),
    [activeMissionDashboard, activeThreadId],
  );
  const isRunCaseEmpty = runCases.length === 0 || !selectedRunCase;
  const scoringConfig = study?.scoringConfig ?? DEFAULT_SCORING_CONFIG;
  const runDisabledReason = getRunDisabledReason({
    isMissionScoped: Boolean(scopedMissionId),
    p2Audit,
    p3Audit,
  });

  useEffect(() => {
    if (runCases.length === 0) {
      setSelectedRunCaseId(null);
      return;
    }

    const hasSelectedRunCase = runCases.some((runCase) => runCase.id === selectedRunCaseId);
    if (!hasSelectedRunCase) setSelectedRunCaseId(runCases[0].id);
  }, [runCases, selectedRunCaseId]);

  useEffect(() => {
    if (!dashboard) {
      setActiveMissionId(null);
      setActiveThreadId(null);
      return;
    }

    if (scopedMissionId) {
      const nextActiveMissionId = dashboard.missions.some((mission) => mission.missionId === scopedMissionId)
        ? scopedMissionId
        : null;
      if (nextActiveMissionId !== activeMissionId) {
        setActiveMissionId(nextActiveMissionId);
        setActiveThreadId(null);
        return;
      }

      const activeMission = dashboard.missions.find((mission) => mission.missionId === nextActiveMissionId) ?? null;
      const hasActiveThread =
        activeMission?.threadVerdicts.some((thread) => thread.threadId === activeThreadId) ?? false;
      if (!hasActiveThread) {
        setActiveThreadId(
          activeMission?.activeThreadId ?? getDefaultActiveThreadId(activeMission?.threadVerdicts ?? []),
        );
      }
      return;
    }

    const hasActiveMission = dashboard.missions.some((mission) => mission.missionId === activeMissionId);
    const nextActiveMissionId = hasActiveMission
      ? activeMissionId
      : (dashboard.activeMissionId ?? dashboard.missions[0]?.missionId ?? null);
    if (nextActiveMissionId !== activeMissionId) {
      setActiveMissionId(nextActiveMissionId);
      return;
    }

    const activeMission =
      dashboard.missions.find((mission) => mission.missionId === nextActiveMissionId) ?? dashboard.missions[0] ?? null;
    const hasActiveThread = activeMission?.threadVerdicts.some((thread) => thread.threadId === activeThreadId) ?? false;
    if (!hasActiveThread) {
      setActiveThreadId(activeMission?.activeThreadId ?? getDefaultActiveThreadId(activeMission?.threadVerdicts ?? []));
    }
  }, [activeMissionId, activeThreadId, dashboard, scopedMissionId]);

  const handleActiveMissionChange = (missionId: string): void => {
    setActiveMissionId(missionId);
    setActiveThreadId(null);
  };

  const handleInspectThread = (params: { missionId: string; threadId: string }): void => {
    setActiveMissionId(params.missionId);
    setActiveThreadId(params.threadId);
  };

  const runEpochInFlight = useRef(false);
  const handleRunEpoch = async (params: { config: MeiaRunEpochFormValues; isDirty: boolean }): Promise<void> => {
    if (runEpochInFlight.current) return;
    if (runDisabledReason) {
      errorToast({ description: runDisabledReason });
      return;
    }
    runEpochInFlight.current = true;
    try {
      const { config, isDirty } = params;
      const { epoch, ...scoringConfig } = config;
      if (isDirty) await updateStudy({ scoringConfig, studyId });
      const nextRunCase = await createRunCase({
        epoch,
        missionId: scopedMissionId,
        parameterOverrides: null,
        studyId,
      });
      const execution = await runCase({ runCaseId: nextRunCase.id, studyId });
      setSelectedRunCaseId(execution.runCase.id);
      setIsScoringConfigModalOpen(false);
      const runLabel = formatRunCaseLabel({ runCase: execution.runCase });
      if (execution.runCase.status === ScoringRunStatus.COMPLETE) {
        successToast({ description: `${runLabel} completed.` });
      } else if (execution.runCase.status === ScoringRunStatus.FAILED) {
        errorToast({ description: `${runLabel} failed. Check validation results for details.` });
      } else {
        successToast({ description: `${runLabel} finished as ${runStatusLabel(execution.runCase.status)}.` });
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Run failed.';
      errorToast({ description: message });
    } finally {
      runEpochInFlight.current = false;
    }
  };

  const handleRefreshRunCases = (): void => {
    void refetchRunCases();
  };

  return (
    <div className="flex flex-col gap-4">
      {scopedMissionId && runCases.length > 0 && activeMissionDashboard && (
        <ThreadVerdictPicker
          activeThreadId={activeThreadDashboard?.threadId ?? null}
          canEdit={canEdit}
          missionId={scopedMissionId}
          onChange={setActiveThreadId}
          studyId={studyId}
          threads={activeMissionDashboard.threadVerdicts}
        />
      )}

      <Tile
        emptyState={{
          message: canEdit ? (runDisabledReason ?? 'Run Epoch') : 'No run cases available.',
          onClick: canEdit && !runDisabledReason ? () => setIsScoringConfigModalOpen(true) : undefined,
          title: 'No Run Cases',
        }}
        isEmpty={isRunCaseEmpty}
        isLoading={isRunCasesLoading}
      >
        {!isRunCaseEmpty && (
          <RunCaseHeader
            canEdit={canEdit}
            isLatest={runCases[0]?.id === selectedRunCase.id}
            isRunDisabled={Boolean(runDisabledReason)}
            onOpenScoringConfig={() => setIsScoringConfigModalOpen(true)}
            onRefresh={handleRefreshRunCases}
            onSelect={setSelectedRunCaseId}
            runCases={runCases}
            runDisabledReason={runDisabledReason}
            runningRunCaseId={isRunning ? runningVariables?.runCaseId : undefined}
            selectedRunCase={selectedRunCase}
          />
        )}
      </Tile>

      {runCases.length > 0 && view === 'runcases' && !scopedMissionId && dashboard && (
        <MeiaP4KillwebVerdictTable
          missions={dashboard.missions}
          onInspect={handleInspectThread}
        />
      )}

      {runCases.length > 0 && (
        <RunCaseDashboard
          activeMission={activeMissionDashboard}
          activeThread={activeThreadDashboard}
          activeThreadVerdict={activeThreadVerdict}
          canEdit={canEdit}
          dashboard={dashboard}
          isLoading={isDashboardLoading}
          isMissionScoped={Boolean(scopedMissionId)}
          onActiveMissionChange={handleActiveMissionChange}
          onActiveThreadChange={setActiveThreadId}
          runCase={selectedRunCase}
          scoringConfig={scoringConfig}
          showThreadTabs={!scopedMissionId}
          view={view}
        />
      )}

      {runCases.length > 0 && view === 'runcases' && dashboard && <MeiaP4GateStatusPanel gates={dashboard.gateCards} />}

      {isScoringConfigModalOpen && (
        <MeiaScoringConfigModal
          config={{ ...scoringConfig, epoch: selectedRunCase?.epoch ?? DEFAULT_RUN_EPOCH }}
          isRunning={isRunning || isCreatingRunCase || isSavingScoringConfig}
          isOpen={isScoringConfigModalOpen}
          onClose={() => setIsScoringConfigModalOpen(false)}
          onRunCase={handleRunEpoch}
        />
      )}
    </div>
  );
};

const getActiveMissionDashboard = (params: {
  activeMissionId: string | null;
  dashboard?: StudyRunCaseDashboardSelectDto;
  fallbackToFirst?: boolean;
}): StudyRunCaseDashboardMissionDto | null => {
  const { activeMissionId, dashboard, fallbackToFirst = true } = params;
  if (!dashboard) return null;
  const activeMission = dashboard.missions.find((mission) => mission.missionId === activeMissionId) ?? null;
  if (activeMission || !fallbackToFirst) return activeMission;
  return dashboard.missions[0] ?? null;
};

const getActiveThreadDashboard = (params: {
  activeMission?: StudyRunCaseDashboardMissionDto | null;
  activeThreadId: string | null;
}): StudyRunCaseDashboardThreadDetailDto | null => {
  const { activeMission, activeThreadId } = params;
  if (!activeMission) return null;
  return activeMission.threads.find((thread) => thread.threadId === activeThreadId) ?? activeMission.threads[0] ?? null;
};

const getActiveThreadVerdict = (params: {
  activeMission?: StudyRunCaseDashboardMissionDto | null;
  activeThreadId: string | null;
}): StudyRunCaseDashboardThreadVerdictDto | null => {
  const { activeMission, activeThreadId } = params;
  if (!activeMission) return null;
  return (
    activeMission.threadVerdicts.find((thread) => thread.threadId === activeThreadId) ??
    activeMission.threadVerdicts[0] ??
    null
  );
};

const getRunDisabledReason = (params: {
  isMissionScoped: boolean;
  p2Audit?: WorkflowGateAuditSelectDto;
  p3Audit?: WorkflowGateAuditSelectDto;
}): string | undefined => {
  const { isMissionScoped, p2Audit, p3Audit } = params;
  if (!isMissionScoped) return undefined;
  if (!p2Audit || !p3Audit) return 'Loading scoring readiness.';
  const threadsDesign = getMeiaPhaseReadinessStatus(p2Audit);
  if (threadsDesign.variant !== 'success') return 'Finish Threads Design before running scoring.';
  const tasksCanvas = getMeiaPhaseReadinessStatus(p3Audit);
  if (tasksCanvas.variant !== 'success') return 'Finish Tasks Canvas before running scoring.';
  return undefined;
};
